
import DOMPurify from 'dompurify'
// @mui material components
import Card from '@mui/material/Card'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDAlert from 'components/MDAlert'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDButton from 'components/MDButton'
import { Tooltip, IconButton } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { AbilityContext, Can } from 'Can'
import { useAbility } from '@casl/react'

function PromptTemplate() {
  let { state } = useLocation()

  const navigate = useNavigate()
  const ability = useAbility(AbilityContext)
  const [data, setData] = useState([])
  const [tableData, setTableData] = useState([])
  const [notification, setNotification] = useState({
    value: false,
    text: '',
  })
  const [expandedRow, setExpandedRow] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        setIsLoading(true)
        const response = await CrudService.getAllPromptTemplate()
        setData(response.data)
        setIsLoading(false)
      } catch(error){
        console.error(error)
        setIsLoading(false)
      }
    })()
    document.title = `Rivio | Prompt Template`
  }, [])

  useEffect(() => {
    setTableData(getRows(data))
  }, [data])

  useEffect(() => {
    if (!state) return
    setNotification({
      value: state.value,
      text: state.text,
    })
  }, [state])

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: '',
        })
      }, 5000)
    }
  }, [notification])

  // Handler to toggle div display
  const handleCellClick = (rowId) => {
    // Toggle the expanded row on click
    setExpandedRow((prev) => (prev === rowId ? null : rowId))
  }

  const clickAddHandler = () => {
    navigate('/prompt-template/new-template')
  }

  const clickEditHandler = async (id) => {

    navigate(`/prompt-template/edit-template/${id}`)
  }

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm('Are you sure you want to delete this template?')) {
        e.nativeEvent.stopImmediatePropagation()
      } else {
        setIsLoading(true)
        await CrudService.removePromptTemplate(id)
        // the delete does not send a response
        // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
        const response = await CrudService.getAllPromptTemplate()
        setData(response.data)
        setNotification({
          value: true,
          text: 'The template has been successfully deleted',
        })
        setIsLoading(false)
      }
    } catch (err) {
      // it sends error is the message is associated with an message
      console.error(err)
      if (err.hasOwnProperty('msg')) {
        setNotification({
          value: true,
          text: err.msg,
        })
      }
      setIsLoading(false)
      return null
    }
  }

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        id: row.prompt_id,
        name: row.name,
        content: row.content,
      }
    })
    return updatedInfo
  }

  const dataTableData = {
    columns: [
      { Header: 'ID', accessor: 'id', width: '5%' },
      { Header: 'Name', accessor: 'name', width: '25%' },
      {
        Header: 'Content',
        accessor: '',
        Cell: (info) => {
          const rowId = info.cell.row.original.id
          const isExpanded = expandedRow === rowId

          return (
            <div>
              <div
                onClick={() => handleCellClick(rowId)}
                style={{ cursor: 'pointer' }}
              >
                {!isExpanded && (
                  <div
                    style={{
                      whiteSpace: 'normal',
                      maxWidth: '300px',
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 3 /* Number of lines */,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        info.cell.row.original.content,
                      ),
                    }}
                  ></div>
                )}
              </div>
              {isExpanded && (
                <div
                  onClick={() => handleCellClick(rowId)}
                  style={{
                    whiteSpace: 'normal',
                    maxWidth: '300px',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(info.cell.row.original.content),
                  }}
                ></div>
              )}
            </div>
          )
        },
      },
      {
        Header: 'actions',
        disableSortBy: true,
        accessor: '',
        Cell: (info) => {
          return (
            <MDBox display="flex" alignItems="center">
              <Tooltip title="Edit Template">
                <IconButton
                  onClick={() => clickEditHandler(info.cell.row.original.id)}
                >
                  <MDTypography>
                    <EditIcon />
                  </MDTypography>
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Template">
                <IconButton
                  onClick={(e) =>
                    clickDeleteHandler(e, info.cell.row.original.id)
                  }
                >
                  <MDTypography>
                    <DeleteIcon />
                  </MDTypography>
                </IconButton>
              </Tooltip>
            </MDBox>
          )
        },
      },
    ],

    rows: tableData,
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">
            {notification.text || ''}
          </MDTypography>
        </MDAlert>
      )}
      <MDBox pt={1}>
        <MDBox>
          <Card>
            <MDBox
              p={3}
              lineHeight={1}
              display="flex"
              justifyContent="space-between"
            >
              <MDTypography variant="h5" fontWeight="medium">
                Prompt Template
              </MDTypography>

              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                type="submit"
                onClick={clickAddHandler}
              >
                + Add Prompt
              </MDButton>
            </MDBox>
            <DataTable table={dataTableData} canSearch={true} loader={isLoading} />
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default PromptTemplate
